import { createContext, useContext, useState, useEffect, useRef } from 'react';

interface AppBarContextType {
  isAppbarOpen: boolean;
  setIsAppbarOpen: (isOpen: boolean) => void;
}

const AppBarContext = createContext<AppBarContextType | undefined>(undefined);
AppBarContext.displayName = 'AppBarContext';

interface Props {
  children: React.ReactNode;
}

export const AppBarProvider: React.FC<Props> = ({ children }) => {
  const [isAppbarOpen, setIsAppbarOpen] = useState(localStorage.getItem('isAppbarOpen') === 'true');
  const observerRef = useRef<ResizeObserver | null>(null);
  const isAppbarOpenRef = useRef(isAppbarOpen);
  const isInitialMount = useRef(true);

  useEffect(() => {
    isAppbarOpenRef.current = isAppbarOpen;
    localStorage.setItem('isAppbarOpen', isAppbarOpen.toString());
  }, [isAppbarOpen]);

  useEffect(() => {
    observerRef.current = new ResizeObserver(() => {
      if (isAppbarOpenRef.current && !isInitialMount.current) {
        setIsAppbarOpen(false);
      }
      isInitialMount.current = false;
    });

    observerRef.current.observe(document.body);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return <AppBarContext.Provider value={{ isAppbarOpen, setIsAppbarOpen }}>{children}</AppBarContext.Provider>;
};

export const useAppBarContext = (): AppBarContextType => {
  const context = useContext(AppBarContext);
  if (!context) {
    throw new Error('useAppBarContext must be used within an AppBarProvider');
  }
  return context;
};
